import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useGetProsumerProfile, useTeamsProfile } from '../../hooks/use-hydra';
import Button from '../../components/elements/Button';
import { H1, H2, P } from '../../components/elements/Text';
import PageWrapper from '../../components/PageWrapper';
import PageWithAsyncData from '../../components/PageWithAsyncData';
import Navbar from '../../components/Navbar';
import { Helmet } from 'react-helmet-async';
import { getHomePageTitle, ROUTES } from '../../routing';
import day from 'dayjs';
import utc from 'dayjs/plugin/utc';
import * as Analytics from '../../analytics';
import { usePreferences } from '../../contexts/preferences';
import autoAnimate from '@formkit/auto-animate';
import { useHistory } from 'react-router-dom';

import ProsumerHome from '../../components/Home/ProsumerHome';
import TeamsHome from '../../components/Home/TeamsHome';
import { useEffectOnce } from 'react-use';
import useAuth from '../../hooks/use-auth';

day.extend(utc);

const ScrollablePageWrapper = styled.div`
  height: calc(100vh - 56px);
  overflow-y: scroll;
  overflow-x: hidden;
  width: 100%;
`;

const PageContentWrapper = styled(PageWrapper)`
  max-width: 1100px;
  margin: 0 auto 82px;
`;

function Home() {
  const teamsQuery = useTeamsProfile();
  const prosumerQuery = useGetProsumerProfile();

  Analytics.useTrackViewOnMount(Analytics.EVENTS.COMMON.VIEW_HOME);

  // we do "!campQuery.value" to check isloading such that we don't render the loader while re-fetching the data
  // this action makes the open onboarding modal to close when moving through the onboarding steps
  const isPageLoading =
    prosumerQuery.loading || (teamsQuery.loading && !teamsQuery.value);

  return (
    <PageWithAsyncData
      isLoading={isPageLoading}
      error={teamsQuery.error || prosumerQuery.error}
      retry={() => {
        teamsQuery.retry();
        prosumerQuery.retry();
      }}
      className={''}
    >
      <Helmet>
        <title>{getHomePageTitle()}</title>
      </Helmet>
      <Navbar
        breadCrumbs={[
          {
            label: 'Home',
            url: null,
          },
        ]}
      />
      <Contents />
    </PageWithAsyncData>
  );
}

const Contents = () => {
  const { value: prosumerProfile, loading: isLoadingProsumerProfile } =
    useGetProsumerProfile();
  const { value: teamsProfile, loading: isLoadingTeamsProfile } =
    useTeamsProfile();
  const { preferences, updatePreferences } = usePreferences();
  const homeContainerRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { user } = useAuth();

  const hasTeamsProfile = teamsProfile?.hasProfile;
  const hasProsumerProfile = !!prosumerProfile;

  // Initialize auto-animate
  useEffect(() => {
    if (homeContainerRef.current) {
      autoAnimate(homeContainerRef.current);
    }
  }, []);

  useEffectOnce(() => {
    const shouldForceTeamsHome = hasTeamsProfile && !preferences.hasSeenTeams;
    const shouldForceSoloHome = hasProsumerProfile && !hasTeamsProfile;

    if (preferences.defaultProfile === null) {
      if (hasTeamsProfile) {
        updatePreferences({
          defaultProfile: 'teams',
          hasSeenTeams: true,
        });
      } else if (hasProsumerProfile) {
        updatePreferences({
          defaultProfile: 'solo',
        });
      }
    } else if (shouldForceTeamsHome) {
      updatePreferences({
        defaultProfile: 'teams',
        hasSeenTeams: true,
      });
    } else if (shouldForceSoloHome) {
      updatePreferences({
        defaultProfile: 'solo',
      });
    }
  });

  // logic similar to LearnModeOnlyRoute, but moved inside here as we use Home for both solo and teams
  React.useEffect(() => {
    const willNotShowTeams = !(hasTeamsProfile && !preferences.hasSeenTeams);
    const isNotLoading = !isLoadingTeamsProfile && !isLoadingProsumerProfile;
    const isProsumerWithoutAccess =
      hasProsumerProfile && !prosumerProfile?.learnMode.hasAccess;

    if (
      preferences.defaultProfile === 'solo' &&
      willNotShowTeams &&
      isNotLoading &&
      isProsumerWithoutAccess
    ) {
      history.push(ROUTES.PROSUMER.PLAN_UPSELL.getPath());
    }

    if (
      (user?.roles.includes('ADMIN') || user?.roles.includes('MANAGER')) &&
      !hasTeamsProfile &&
      !hasProsumerProfile
    ) {
      history.push(ROUTES.ADMIN.COMPANIES.getPath());
    }
  }, [
    preferences.defaultProfile,
    isLoadingTeamsProfile,
    isLoadingProsumerProfile,
    hasProsumerProfile,
    updatePreferences,
    history,
    prosumerProfile,
    preferences.hasSeenTeams,
    hasTeamsProfile,
    user?.roles,
  ]);

  // If defaultProfile is null, render nothing
  if (preferences.defaultProfile === null) {
    return null;
  }

  return (
    <ScrollablePageWrapper>
      <PageContentWrapper ref={homeContainerRef}>
        <H1>Welcome!</H1>
        {preferences.defaultProfile === 'teams' ? (
          <TeamsHome />
        ) : preferences.defaultProfile === 'solo' ? (
          <ProsumerHome />
        ) : null}
        <Help />
      </PageContentWrapper>
    </ScrollablePageWrapper>
  );
};

function Help() {
  return (
    <>
      <hr style={{ width: '100%' }} />
      <H2>Have any questions?</H2>
      <P>E-mail us via the button below.</P>
      <div style={{ width: 'fit-content' }}>
        <Button
          label="Get in touch"
          type="primary"
          onClick={() => window.open('mailto:concierge@enki.com')}
        />
      </div>
    </>
  );
}

export default Home;
